import React from 'react';

import Seo from '../ui/Seo';
import PageHero from '../sections/PageHero';
import TradeTicker from '../sections/TradeTicker';
import Pledge from '../sections/Pledge';
import StoryDeck from '../sections/StoryDeck';
import SectionHead from '../sections/SectionHead';
import Faqs from '../sections/Faqs';
import Cta from '../sections/Cta';

import DiagnosisRepairs from "../media/images/commercial-services/diagnosis-repairs.jpg";
import InstallationsUpgrades from "../media/images/commercial-services/installations-upgrades.jpg";
import SpecialtyLighting from "../media/images/commercial-services/specialty-lighting.jpg";
import NewConstruction from "../media/images/commercial-services/new-construction.jpg";
import ParkingLotLighting from "../media/images/commercial-services/parking-lot-lighting.jpg";
import LandscapeSecurityWiring from "../media/images/commercial-services/landscape-security-wiring.jpg";
import WarehouseLighting from "../media/images/commercial-services/warehouse-lighting.jpg";
import OfficeRenovations from "../media/images/commercial-services/office-renovations.jpg";
import CodeUpgradesCorrections from "../media/images/commercial-services/code-upgrades-corrections.jpg";
import ExitEmergencyLighting from "../media/images/commercial-services/exit-emergency-lighting.jpg";
import SurgeProtection from "../media/images/commercial-services/surge-protection.jpg";
import EvChargingStations from "../media/images/commercial-services/ev-charging-stations.jpg";

const content =
[
    {
        title: "Diagnosis & Repairs",
        text: "Electrical issues in a commercial building can disrupt operations and pose safety risks. At Smart Electric Solutions, our experienced electricians specialize in same-day diagnosis and repairs to minimize downtime. From troubleshooting complex problems to resolving simple issues, we provide reliable and long-lasting solutions tailored to your needs.",
        imgSrc: DiagnosisRepairs,
        alt: "Smart Electric Solutions - Diagnosis and Repairs",
        focus: "50%",
    },
    {
        title: "Installations & Upgrades",
        text: "For businesses needing electrical installations or system upgrades, Smart Electric Solutions is your trusted partner. We handle everything from installing new systems to upgrading outdated ones. Our services are designed to enhance safety, efficiency, and reliability while ensuring the job is done right the first time.",
        imgSrc: InstallationsUpgrades,
        alt: "Smart Electric Solutions - Installations and Upgrades",
        focus: "50%",
    },
    {
        title: "Specialty Lighting",
        text: "Transform your business with customized lighting solutions from Smart Electric Solutions. Whether it’s energy-efficient LEDs, storefront lighting, or bespoke indoor and outdoor designs, we collaborate with you to create the ideal lighting setup that matches your vision and enhances your property’s appeal.",
        imgSrc: SpecialtyLighting,
        alt: "Smart Electric Solutions - Specialty Lighting",
        focus: "20%",
    },
    {
        title: "New Construction",
        text: "Starting a new business or relocating? Smart Electric Solutions offers tailored electrical services for commercial new constructions. From wiring installations to energy-efficient solutions, we ensure your building is set up for long-term safety, compliance, and functionality, no matter the project size.",
        imgSrc: NewConstruction,
        alt: "Smart Electric Solutions - New Construction",
        focus: "90%",
    },
    {
        title: "Parking Lot Lighting",
        text: "Keep your business safe and inviting with professional parking lot and pathway lighting from Smart Electric Solutions. We install, repair, and upgrade lighting systems to create a secure environment for your employees and visitors. Our solutions ensure compliance with regulations and lasting performance.",
        imgSrc: ParkingLotLighting,
        alt: "Smart Electric Solutions - Parking Lot Lighting",
        focus: "70%",
    },
    {
        title: "Landscape & Security Wiring",
        text: "Enhance your property’s safety with reliable landscape and security wiring solutions. Smart Electric Solutions provides professional installations for motion sensors, alarm systems, and security lighting, ensuring seamless protection for your business and peace of mind for you and your team.",
        imgSrc: LandscapeSecurityWiring,
        alt: "Smart Electric Solutions - Landscape and Security Wiring",
        focus: "50%",
    },
    {
        title: "Warehouse Lighting",
        text: "Optimized lighting can improve productivity and safety in warehouses. Smart Electric Solutions offers expert installation and repair services for lighting systems tailored to your space. We help you enhance visibility and efficiency while ensuring compliance with electrical codes.",
        imgSrc: WarehouseLighting,
        alt: "Smart Electric Solutions - Warehouse Lighting",
        focus: "50%",
    },
    {
        title: "Office Renovations",
        text: "Upgrade your office with energy-efficient electrical systems installed by Smart Electric Solutions. From wiring installations to customized lighting, we ensure your office renovation meets modern standards for efficiency and compliance, creating a workspace that’s both inviting and productive.",
        imgSrc: OfficeRenovations,
        alt: "Smart Electric Solutions - Office Renovations",
        focus: "30%",
    },
    {
        title: "Code Upgrades & Code Corrections",
        text: "Ensuring compliance with building codes is vital for business safety and operation. Smart Electric Solutions identifies and resolves code violations, providing expert corrections to keep your property up to date. Trust us to deliver safe, reliable upgrades tailored to your building’s needs.",
        imgSrc: CodeUpgradesCorrections,
        alt: "Smart Electric Solutions - Code Upgrades and Corrections",
        focus: "50%",
    },
    {
        title: "Exit & Emergency Lighting",
        text: "Exit and emergency lighting are crucial for the safety of any commercial property. At Smart Electric Solutions, we install and maintain systems that ensure compliance with legal requirements and guarantee safe evacuation in emergencies. Trust our expertise for reliable installations and support.",
        imgSrc: ExitEmergencyLighting,
        alt: "Smart Electric Solutions - Exit and Emergency Lighting",
        focus: "50%",
    },
    {
        title: "Surge Protection",
        text: "Protect your business from power surges with professional surge protection services. Smart Electric Solutions installs systems that safeguard your equipment and electrical systems from voltage spikes, minimizing risks and ensuring long-lasting protection for your investments.",
        imgSrc: SurgeProtection,
        alt: "Smart Electric Solutions - Surge Protection",
        focus: "50%",
    },
    {
        title: "EV Charging Stations",
        text: "Prepare your property for the future with EV charging stations installed by Smart Electric Solutions. As electric vehicles become more common, providing charging options for customers and employees helps meet sustainability goals while adding value to your business.",
        imgSrc: EvChargingStations,
        alt: "Smart Electric Solutions - EV Charging Stations",
        focus: "60%",
    }
];

export default function CommercialServices() {
    return (
        <>
            <Seo
                title="Commercial Electrical Services"
                description="Commercial electrical services across South Florida: installations and upgrades, specialty and warehouse lighting, new construction, code corrections, surge protection and EV charging."
                keywords="commercial electrician, warehouse lighting, parking lot lighting, code corrections, surge protection, EV charging stations, office renovations, South Florida"
                path="/commercial-services"
                schema={{
                    '@type': 'WebPage',
                    name: 'Commercial Electrical Services',
                    description: 'Commercial electrical services across South Florida: installations and upgrades, specialty and warehouse lighting, new construction, code corrections, surge protection and EV charging.',
                    url: 'https://smartelectric.solutions/commercial-services'
                }}
            />

            <PageHero
                eyebrow="Spaces"
                title="Commercial power, on the schedule you actually have."
                lead="Fit-outs, retrofits and new construction for buildings that cannot afford a dark day. We work to opening dates and pass inspection the first time."
                backdrop={content[0].imgSrc}
                focus={`${content[0].focus} center`}
                crumbs={[{ label: 'Spaces' }, { label: 'Commercial Services' }]}
            />

            <TradeTicker />

            <section className="section section--tight">
                <div className="shell">
                    <Pledge />
                </div>
            </section>

            <section className="section section--ruled">
                <div className="shell">
                    <SectionHead
                        eyebrow="What we do"
                        title="Commercial work, end to end."
                        lead="Distribution, lighting, life safety and the code corrections that stand between a building and its certificate of occupancy."
                    />
                </div>

                <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                    <StoryDeck items={content} />
                </div>
            </section>

            <Faqs />
            <Cta />
        </>
    );
}
